import type {AxiosError} from 'axios';
import {useRouter} from 'next/router';
import {useQuery, useQueryClient, type QueryFunctionContext, type UseQueryOptions} from '@tanstack/react-query';

import {api} from '@eksab/api';
import {useAccessToken} from '@eksab/hooks/useAccessToken';

import type {Challenge} from '../types';

const queryKey = (challengeID: string | undefined, isLoggedIn: boolean, locale: Locale) =>
  ['trivia-challenge', challengeID, isLoggedIn, locale] as const;

type QueryKey = ReturnType<typeof queryKey>;

const queryFn = ({queryKey: [, challengeID, isLoggedIn]}: QueryFunctionContext<QueryKey>) =>
  api
    .get<{data: Challenge | null}>(`v1/challenges/${challengeID}${isLoggedIn ? '/auth' : ''}`)
    .then((res) => res.data.data);

export const useChallengeQuery = (
  challengeID: string | undefined,
  options?: UseQueryOptions<Challenge | null, AxiosError, Challenge | null, QueryKey>,
) => {
  const {locale} = useRouter();
  const isLoggedIn = useAccessToken().isSuccess;

  return useQuery<Challenge | null, AxiosError, Challenge | null, QueryKey>(
    queryKey(challengeID, isLoggedIn, locale),
    queryFn,
    {
      ...options,
      enabled: !!challengeID && (options?.enabled ?? true),
    },
  );
};

export function useSetChallengeQueryData() {
  const {locale} = useRouter();
  const queryClient = useQueryClient();
  const isLoggedIn = useAccessToken().isSuccess;

  return (challenge: Challenge) => queryClient.setQueryData(queryKey(challenge.id, isLoggedIn, locale), challenge);
}

useChallengeQuery.queryKey = queryKey;
useChallengeQuery.queryFn = queryFn;
